import dayjs from "dayjs"
import { MutationResult } from "@/api/utils/types"
import { queryClient, useMutation } from "@/api/utils/useQuery"
import { processReadingErrors } from "./processReadingErrors"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import { insertReadingsIntoMeterpointCache } from "./insertReadingsIntoMeterpointCache"
import { IMeterpoint } from "@/models/meterpoint/meterpoint.types"
import { ISubmitReadings } from "./MeterReadingsMutator.types"
import { UpdateStatusAndCommentMutationVariables } from "@/api/hasura/generated/graphql"
import { MeterReadingType } from "@/api/backofficeRest/generated/model"
import { HasuraMutations } from "@/api/hasura/mutations"
import {
  BackofficeRestMutations,
  CreateOutboundPhotoRequestPayload,
  SendOutboundFollowUpPayload,
  SubmitMeterReadingPayload,
  UpdateMeterpointConsentsPayload,
} from "@/api/backofficeRest/mutations"
import { useCurrentAccountStore } from "@/stores/currentAccount"
import { ReadingSequenceType } from "@/models/meterReading/meterReading.types"

// The submit endpoint only accepts First/Normal; the form never offers Last.
const SEQUENCE_TYPE_INPUT: Partial<
  Record<ReadingSequenceType, MeterReadingType>
> = {
  [ReadingSequenceType.First]: MeterReadingType.First,
  [ReadingSequenceType.Normal]: MeterReadingType.Normal,
}

export interface IMeterReadingMutator {
  submitForAccount(): MutationResult<ISubmitReadings>
  updateStatusAndComment(): MutationResult<UpdateStatusAndCommentMutationVariables>
  updateMeterpointReadFreqConsent(): MutationResult<UpdateMeterpointConsentsPayload>
}

const serializeSubmitReadings = (
  data: ISubmitReadings,
): SubmitMeterReadingPayload => {
  const store = useCurrentAccountStore()
  const coreId = store.coreId.data
  if (!coreId) throw new Error("Missing coreId")

  const sequenceType = SEQUENCE_TYPE_INPUT[data.sequenceType]
  if (!sequenceType) {
    throw new Error(`Unsupported reading type: ${data.sequenceType}`)
  }

  const date = dayjs(data.date).format("YYYY-MM-DD")
  return {
    billingAccountId: coreId,
    body: {
      readings: data.readings.map(([reading, register]) => ({
        date,
        reading,
        type: sequenceType,
        meter: data.meter.identifier,
        meterPoint: data.meterpoint.id,
        register: register.identifier,
      })),
    },
  }
}

class MeterReadingMutatorClass implements IMeterReadingMutator {
  submitForAccount() {
    const { mutationFn } = BackofficeRestMutations.readings.submit()

    return useMutation({
      // A rejected submit never reaches errorHandlerFn — that only wraps the
      // synchronous call — so the agent-facing guidance has to be attached
      // here, or the form shows only "Unexpected error occurred".
      mutationFn: async (data: ISubmitReadings) => {
        try {
          return await mutationFn(serializeSubmitReadings(data))
        } catch (error) {
          throw processReadingErrors(error as Error, data) ?? error
        }
      },
      serializerFn: (data: ISubmitReadings) => data,
      onSuccessFn: (_response: void, readingSubmission: ISubmitReadings) => {
        const meterpointQueryKey = BackofficeRestQueries.meterpoints.forAccount(
          readingSubmission.account.number,
        ).queryKey

        queryClient.setQueryData(
          meterpointQueryKey,
          (meterpointCache?: IMeterpoint[]) => {
            if (meterpointCache) {
              return insertReadingsIntoMeterpointCache(
                readingSubmission,
                meterpointCache,
              )
            }
          },
        )
      },
    })
  }

  updateMeterpointReadFreqConsent(): MutationResult<UpdateMeterpointConsentsPayload> {
    return useMutation({
      ...BackofficeRestMutations.meterpoints.updateConsent(),
      serializerFn: (data: UpdateMeterpointConsentsPayload) => data,
      invalidateCache: true,
    })
  }

  updateStatusAndComment(): MutationResult<UpdateStatusAndCommentMutationVariables> {
    return useMutation({
      ...HasuraMutations.readings.updateStatus(),
      serializerFn: (data) => data,
      invalidateCache: true,
    })
  }
  createOutboundPhotoRequest(): MutationResult<CreateOutboundPhotoRequestPayload> {
    return useMutation({
      ...BackofficeRestMutations.readings.createOutboundPhotoRequest(),
      serializerFn: (data: CreateOutboundPhotoRequestPayload) => data,
      invalidateCache: true,
    })
  }

  cancelOutboundRequest(): MutationResult<number> {
    return useMutation({
      ...BackofficeRestMutations.readings.cancelOutboundRequest(),
      serializerFn: (data: number) => data,
      invalidateCache: false,
    })
  }

  sendFollowUpEmailForOutboundRequest(): MutationResult<SendOutboundFollowUpPayload> {
    return useMutation({
      ...BackofficeRestMutations.readings.sendFollowUpEmailForOutboundRequest(),
      serializerFn: (data: SendOutboundFollowUpPayload) => data,
      invalidateCache: false,
    })
  }
}

export const MeterReadingsMutator = new MeterReadingMutatorClass()
